import React, { useState } from "react"
import { Button, Typography, Space, Flex } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import FullySubstitutable from "./FullySubstitutable"
import AssignedExperienceNoActiveBooking from "./AssignedExperienceNoActiveBooking"
import PartiallySubstitutable from "./PartiallySubstitutable"
import { Modal } from "ui/Modal"
import { EQUIPMENT_DELETE_DEACTIVATE_MODES } from "../../constants"
import { spacings } from "themes/variables"
import styles from "./index.module.css"
const { Title, Text } = Typography

const DeleteDeactivateModal = ({
  visible,
  onCancel,
  onOk,
  title,
  subtitle,
  warning,
  list = [],
  okText,
  cancelText,
  loading = false,
  upcomingBookings = [],
  assignedExperiences = [],
  mode,
  expMessage,
  bookingMessage
}) => {
  const { t } = useTranslation()
  const [replacementSelections, setReplacementSelections] = useState({})
  const [allSelected, setAllSelected] = useState(false)
  const [isPartiallyValid, setIsPartiallyValid] = useState(false)
  const [partialReplacements, setPartialReplacements] = useState({})

  const handleSelectionsChange = (selections, isAllSelected) => {
    setReplacementSelections(selections)
    setAllSelected(isAllSelected)
  }

  const handleValidityChange = (isValid) => {
    setIsPartiallyValid(isValid)
  }

  const handlePartialSelectionsChange = (selections) => {
    setPartialReplacements(selections)
  }

  const handleOk = () => {
    // Call onOk with data object based on mode
    // For fully/partially substitutable modes, transform replacements to API format
    const data = {}

    if (mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE && Object.keys(replacementSelections).length > 0) {
      // Transform index-based selections to booking_id → boat_id mapping
      const substitutions = {}
      Object.entries(replacementSelections).forEach(([index, boatId]) => {
        const booking = upcomingBookings[Number.parseInt(index)]
        if (booking?.booking_id) {
          substitutions[booking.booking_id] = boatId
        }
      })
      data.substitutions = substitutions
    } else if (mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE && Object.keys(partialReplacements).length > 0) {
      // Partial replacements are already keyed by booking_id, just rename the key
      data.substitutions = partialReplacements
    }

    onOk(data)
  }

  // Disable OK button for fully substitutable mode if not all selections are made
  // Disable OK button for partially substitutable mode if not valid (no options or not all selected)
  const isOkDisabled =
    (mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE &&
      upcomingBookings.length > 0 &&
      !allSelected) ||
    (mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE &&
      upcomingBookings.length > 0 &&
      !isPartiallyValid)

  return (
    <Modal open={visible} onCancel={onCancel} centered width={1050} className={styles.deleteDeactivateModal} maskClosable={false}>
      <Space direction="vertical" style={{ width: "100%" }} size="middle" className="overflow-auto">
        <Title level={3}>{title}</Title>
        {subtitle && <Text>{subtitle}</Text>}

        {/* Mode-specific content */}
        {mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES && (
          // No additional content needed for NO_EXPERIENCES mode - just title and subtitle
          null
        )}

        {mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING && (
          <AssignedExperienceNoActiveBooking list={list} warning={warning} assignedExperiences={assignedExperiences} />
        )}

        {mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE && (
          <FullySubstitutable
            upcomingBookings={upcomingBookings}
            assignedExperiences={assignedExperiences}
            warning={warning}
            onSelectionsChange={handleSelectionsChange}
          />
        )}

        {mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE && (
          <PartiallySubstitutable
            upcomingBookings={upcomingBookings}
            assignedExperiences={assignedExperiences}
            warning={warning}
            onValidityChange={handleValidityChange}
            onSelectionsChange={handlePartialSelectionsChange}
            expMessage={expMessage}
            bookingMessage={bookingMessage}
          />
        )}

        <Flex gap={spacings.S}>
          <Button variant="outlined" color="primary" onClick={onCancel}>{cancelText || t("global.cancel")}</Button>
          <Button type="primary" danger onClick={handleOk} loading={loading} disabled={isOkDisabled}>
            {okText || t("global.ok")}
          </Button>
        </Flex>
      </Space>
    </Modal>
  )
}

DeleteDeactivateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  warning: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
      avatar: PropTypes.string
    })
  ),
  okText: PropTypes.string,
  cancelText: PropTypes.string,
  loading: PropTypes.bool,
  upcomingBookings: PropTypes.arrayOf(
    PropTypes.shape({
      experienceName: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      clientName: PropTypes.string.isRequired,
      replaceWith: PropTypes.string,
      link: PropTypes.string
    })
  ),
  assignedExperiences: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      link: PropTypes.string
    })
  ),
  mode: PropTypes.oneOf(Object.values(EQUIPMENT_DELETE_DEACTIVATE_MODES)).isRequired,
  expMessage: PropTypes.string,
  bookingMessage: PropTypes.string
}

export default DeleteDeactivateModal
